import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, tap, map } from "rxjs";
import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";
import {
  HearingScheduleQuery,
  HearingSchedulingCalendarContext,
  ProceedingCalendarRow,
  ReadyToScheduleRow,
  ScheduleHearingRequest,
  ScheduleHearingResult,
} from "./hearing-schedule.models";

const CALENDAR_PROJECTION_URL = "/api/v1/scheduling/projections/calendar";
const PROCEEDINGS_PROJECTION_URL = "/api/v1/scheduling/projections/proceedings";
const READY_TO_SCHEDULE_URL = "/api/v1/scheduling/projections/ready-to-schedule";
const DEFAULT_TAKE = "200";

const quietHttpOptions = {
  headers: new HttpHeaders({
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
  }),
  withCredentials: true,
};

// Scheduling WRITES must also bypass the global ErrorInterceptor. That interceptor rewrites a 400
// validation problem (throws the flattened `errors` map as a bare string[]), which destroys the
// HttpErrorResponse the schedule/reschedule dialogs and the flyout move-date handler rely on to show
// the specific rule message — leaving them with a generic toast or the page-level "Unexpected error"
// banner. These callers map the raw failure themselves (mapHearingScheduleError / the dialogs), so
// they skip the interceptor exactly like the reads above; the global loading spinner is left on.
const writeHttpOptions = {
  headers: new HttpHeaders({ "skip-error-interceptor": "yes" }),
  withCredentials: true,
};

@Injectable({ providedIn: "root" })
export class HearingScheduleService {
  private readonly schedulingContexts = new Map<number,HearingSchedulingCalendarContext>();
  private capture(rows: (ProceedingCalendarRow | ReadyToScheduleRow)[]) { for (const row of rows) { if(row.schedulingContext) this.schedulingContexts.set(row.scheduledProceedingId,row.schedulingContext); } }
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(ApplicationConfigurationService);

  getCalendar(query: HearingScheduleQuery = {}): Observable<ProceedingCalendarRow[]> {
    let params = new HttpParams().set("take", DEFAULT_TAKE);
    if (query.startUtc) {
      params = params.set("startUtc", query.startUtc);
    }
    if (query.endUtc) {
      params = params.set("endUtc", query.endUtc);
    }
    if (query.statusCode) {
      params = params.set("statusCode", query.statusCode);
    }

    // Resolve against the absolute API host (apiUrl) rather than a relative path: the dev proxy's
    // /api rule targets a stale port, so relative calls 500. Mirrors the case-context services.
    return this.http.get<ProceedingCalendarRow[]>(this.appConfig.apiUrl(CALENDAR_PROJECTION_URL), {
      ...quietHttpOptions,
      params,
    }).pipe(tap(rows => this.capture(rows)));
  }

  /**
   * Commissioner-only: schedule (add) a hearing onto the calendar (PHMS-SCH-6). POSTs the target
   * window/slot to the scheduling-projection write endpoint, which is server-gated on the
   * workflow_runtime.transition permission. Callers should refresh the calendar on success.
   */
  scheduleHearing(
    scheduledProceedingId: number,
    body: ScheduleHearingRequest,
  ): Observable<ScheduleHearingResult> {
    const managed = body.schedulingContext || this.schedulingContexts.get(scheduledProceedingId);
    if (managed) return this.writeManaged(managed, 'book', body);
    return this.http.post<ScheduleHearingResult>(
      this.appConfig.apiUrl(`${PROCEEDINGS_PROJECTION_URL}/${scheduledProceedingId}/schedule`),
      body,
      writeHttpOptions,
    );
  }

  /**
   * Scheduler-only: reschedule (change) an already-scheduled hearing's date, time, and/or location
   * (story 1445). POSTs the new window/location to the dedicated reschedule write endpoint, which is
   * server-gated on the same workflow_runtime.transition permission and runs the same slot-conflict
   * validation as initial scheduling, and appends a durable schedule-history row recording the prior
   * and new windows. Callers should refresh the calendar on success.
   */
  rescheduleHearing(
    scheduledProceedingId: number,
    body: ScheduleHearingRequest,
  ): Observable<ScheduleHearingResult> {
    const managed = body.schedulingContext || this.schedulingContexts.get(scheduledProceedingId);
    if (managed) return this.writeManaged(managed, 'reschedule', body);
    return this.http.post<ScheduleHearingResult>(
      this.appConfig.apiUrl(`${PROCEEDINGS_PROJECTION_URL}/${scheduledProceedingId}/reschedule`),
      body,
      writeHttpOptions,
    );
  }

  /**
   * Commissioner-only: unschedule (remove) a hearing from the calendar (PHMS-SCH-6). Modeled
   * server-side as a continuance that nulls the slot and records schedule history, preserving the
   * audit trail. Callers should refresh the calendar on success.
   */
  unscheduleHearing(scheduledProceedingId: number): Observable<ScheduleHearingResult> {
    const managed = this.schedulingContexts.get(scheduledProceedingId);
    if (managed) return this.writeManaged(managed, 'remove', { reasonCode: 'Calendar removal' });
    return this.http.post<ScheduleHearingResult>(
      this.appConfig.apiUrl(`${PROCEEDINGS_PROJECTION_URL}/${scheduledProceedingId}/unschedule`),
      {},
      writeHttpOptions,
    );
  }

  private writeManaged(context: HearingSchedulingCalendarContext, operation: string, body: Partial<ScheduleHearingRequest>): Observable<ScheduleHearingResult> {
    const intentId = crypto.randomUUID();
    return this.http.post<ScheduleHearingResult>(this.appConfig.apiUrl(`/api/phms/hearing-scheduling/requests/${context.requestId}/${operation}`), {
      occurrenceId: context.occurrenceId, expectedETag: context.eTag, expectedRevision: context.revision, intentId,
      reason: body.reasonCode || body.schedulingNotes || 'Calendar placement', scheduledStartUtc: body.scheduledStartUtc,
      scheduledEndUtc: body.scheduledEndUtc, timeZoneId: body.timeZoneId, locationName: body.locationName, roomName: body.roomName, calendarSlotId: body.calendarSlotId, videoConferenceUrl: body.videoConferenceUrl, schedulingNotes: body.schedulingNotes,
    }, { ...writeHttpOptions, headers: writeHttpOptions.headers.set('Idempotency-Key', intentId) }).pipe(map(result => ({...result, workflowInstanceId:context.workflowId, schedulingPostureCode:operation === 'remove' ? 'continued' : 'scheduled', scheduledStartUtc:body.scheduledStartUtc, scheduledEndUtc:body.scheduledEndUtc})));
  }

  /**
   * Candidates ready to be placed on the calendar (PHMS-SCH-6). The search bar's Add-to-Calendar flow
   * looks up an inmate's pending proceeding here (by subject key) before scheduling it.
   */
  getReadyToSchedule(): Observable<ReadyToScheduleRow[]> {
    return this.http.get<ReadyToScheduleRow[]>(
      this.appConfig.apiUrl(READY_TO_SCHEDULE_URL),
      quietHttpOptions,
    ).pipe(tap(rows => this.capture(rows)));
  }
}
